import { Id, Items, Matrices, Product } from '@typings';
import { multiply, sum } from 'ramda';

import { isDefined } from '../utils/is';

const enum Measurements {
  TABLE_DEFAULT_ROW_HEIGHT = 58,
  TABLE_HEADER_ROW_HEIGHT = 40,
  PRESETS_LINE_HEIGHT = 40,
  SCROLLBAR_GAP_HEIGHT = 4,
  FILL_IN_INFO_HEIGHT = 28,
}

export const getIsTwoDimensional = (table: Items.Table): table is Items.Table.TwoDimensional => table.x.length >= 1 && table.y.length >= 1;
export const hasOneDimensionOverlap = (itemTable1: Items.Table, itemTable2: Items.Table): boolean => {
  const set1 = new Set(itemTable1.x);

  return itemTable2.x.some(item => set1.has(item));
};

export const getNumberOfRowsInMatrixToRender = (variants: Product.Standard[]): number => {
  const [firstVariant] = variants;

  if (!isDefined(firstVariant)) {
    return 0;
  }

  if (getIsTwoDimensional(firstVariant.itemTable)) {
    return firstVariant.itemTable.y.length;
  }

  return variants.length;
};

interface ProductMatrixConfig {
  variants: Product.Standard[];
  hasControls: boolean;
  hasSummaries: boolean;
  isDesktop: boolean;
}

export const getEstimatedProductMatrixHeight = (
  { variants, hasControls, hasSummaries, isDesktop }: ProductMatrixConfig,
  modifierFn = (height: number): number => height,
): number => {
  return modifierFn(
    sum([
      Measurements.TABLE_HEADER_ROW_HEIGHT,
      multiply(Measurements.TABLE_DEFAULT_ROW_HEIGHT)(getNumberOfRowsInMatrixToRender(variants)),
      hasSummaries ? Measurements.TABLE_HEADER_ROW_HEIGHT : 0,
      Measurements.SCROLLBAR_GAP_HEIGHT,
      hasControls ? Measurements.PRESETS_LINE_HEIGHT : 0,
      isDesktop ? 0 : Measurements.FILL_IN_INFO_HEIGHT,
    ]),
  );
};

export const getExtraSizeInfo = (variant: Product.Standard, itemId: Id, localizationDefinitionName?: string) => {
  if (!isDefined(localizationDefinitionName)) {
    return {};
  }

  const localizedSize = variant.items.find(item => item.item === itemId)?.name ?? '';

  return {
    localizedProdSize: {
      localizationDefinitionName,
      localizedSize,
    },
  };
};

export const filterEmptyVariants = (
  cellQuantitiesMap: Matrices.CellQuantities[][],
  variants: (Product.Standard | Product.Full)[],
): (Product.Standard | Product.Full)[] => {
  const variantTotalQuantities = cellQuantitiesMap.flat().reduce((totals, cell) => {
    const { variantId, quantity } = cell;
    const currentTotal = totals.get(variantId) || 0;
    totals.set(variantId, currentTotal + quantity);

    return totals;
  }, new Map<Id, number>());

  return variants.filter(variant => {
    const totalQuantity = variantTotalQuantities.get(variant.variant) || 0;

    return totalQuantity > 0;
  });
};
